import React, { type ComponentType, type ReactNode } from 'react';
import Markdoc, { type RenderableTreeNode } from '@markdoc/markdoc';
import {
  defaultComponents,
  mergeComponents,
  type PortableTextComponents,
} from '@portabletext/react';
import { emailMarkdocComponents } from 'components/EmailLink/richText';

type Component = ComponentType<any>;
const markNames: Record<string, string> = {
  a: 'link',
  strong: 'strong',
  em: 'em',
  u: 'underline',
  del: 'strike-through',
  SourceCenter: 'center',
  SourceHighlight: 'highlight',
  SourceStrike: 'strike-through',
  SourceInternalLink: 'internalLink',
};

/** Present validated Markdoc with the same components as its original Portable Text surface. */
export function renderPortablePresentation(
  tree: RenderableTreeNode,
  overrides: PortableTextComponents,
  imageUrl: (source: string) => string,
  onImageError?: () => void,
) {
  const components = mergeComponents(defaultComponents, overrides);
  const select = (value: unknown, name: string): Component | undefined =>
    typeof value === 'function'
      ? (value as Component)
      : (value as Record<string, Component> | undefined)?.[name];

  function render(
    node: RenderableTreeNode,
    index: number,
    parent?: string,
    laterParagraph = false,
  ): ReactNode {
    if (Array.isArray(node))
      return node.map((child, childIndex) => render(child, childIndex, parent));
    if (!Markdoc.Tag.isTag(node)) return node as ReactNode;
    const { name, attributes, children } = node;
    const isParagraph = (child: RenderableTreeNode) =>
      Markdoc.Tag.isTag(child) && child.name === 'p';
    const firstParagraph = children.findIndex(isParagraph);
    const splitsQuote = name === 'blockquote' && children.length > 1 && children.every(isParagraph);
    const rendered = children.map((child, childIndex) =>
      render(child, childIndex, name, !splitsQuote && childIndex > firstParagraph),
    );
    const element = (Component: Component | string | undefined, value = {}) =>
      Component ? (
        React.createElement(Component, {
          key: index,
          ...(typeof Component === 'string' ? {} : { value }),
          children: rendered,
        })
      ) : (
        <React.Fragment key={index}>{rendered}</React.Fragment>
      );

    // Portable Text renders a body as sibling blocks, with no extra <article> wrapper.
    if (name === 'article') return <React.Fragment key={index}>{rendered}</React.Fragment>;
    if (name === 'p' && (parent === 'li' || parent === 'blockquote'))
      // Native Markdoc can hold several paragraphs in one list item or quote. A later one
      // needs its own line, but a <p> would move a list marker and add `prose` quote marks.
      // The space keeps the words apart in plain text and for crawlers.
      return laterParagraph ? (
        <React.Fragment key={index}>
          {' '}
          <span className="mt-2 block">{rendered}</span>
        </React.Fragment>
      ) : (
        <React.Fragment key={index}>{rendered}</React.Fragment>
      );
    // Markdown groups adjacent quotes, while each Portable Text quote was a separate block.
    if (splitsQuote) {
      const Blockquote = select(components.block, 'blockquote');
      return (
        <React.Fragment key={index}>
          {rendered.map((child, childIndex) =>
            Blockquote ? (
              <Blockquote key={childIndex} value={{ style: 'blockquote' }}>
                {child}
              </Blockquote>
            ) : (
              child
            ),
          )}
        </React.Fragment>
      );
    }
    if (/^h[1-6]$/.test(name) || name === 'p' || name === 'blockquote') {
      const style = name === 'p' ? 'normal' : name;
      return element(select(components.block, style), { style });
    }
    if (name === 'ul' || name === 'ol')
      return element(select(components.list, name === 'ol' ? 'number' : 'bullet'));
    if (name === 'li')
      return element(select(components.listItem, parent === 'ol' ? 'number' : 'bullet'));
    if (markNames[name])
      return element(select(components.marks, markNames[name]) || 'span', {
        ...attributes,
        ...(name === 'SourceInternalLink' ? { slug: { current: attributes.slug } } : {}),
      });
    if (name === 'ContentImage') {
      const { src, alt, width, height, crop, hotspot, asset } = attributes;
      return element(components.types.image, {
        _type: 'image',
        asset: { _type: 'reference', _ref: asset },
        alt,
        localizedAsset: { src: imageUrl(src), width, height },
        onImageError,
        ...(crop ? { crop: JSON.parse(crop) } : {}),
        ...(hotspot ? { hotspot: JSON.parse(hotspot) } : {}),
      });
    }
    if (name === 'ContentTable')
      return element(components.types[attributes.sourceType === 'tables' ? 'tables' : 'table'], {
        _type: attributes.sourceType === 'tables' ? 'tables' : 'table',
        ...(attributes.caption ? { caption: attributes.caption } : {}),
        rows: (JSON.parse(attributes.rows) as string[][]).map((cells) => ({ cells })),
        table: { rows: (JSON.parse(attributes.rows) as string[][]).map((cells) => ({ cells })) },
      });
    if (name === 'NewsletterCta') return element(components.types.newsletterCta);
    const Email = emailMarkdocComponents[name as keyof typeof emailMarkdocComponents];
    if (Email)
      return React.createElement(Email as Component, { ...attributes, key: index }, rendered);
    const { class: className, ...rest } = attributes;
    return React.createElement(
      name,
      { ...rest, ...(className ? { className } : {}), key: index },
      ...rendered,
    );
  }
  return render(tree, 0);
}
